import { computed, inject, Injectable, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import {
    CompanyController,
    UsageCategorySummary,
    UsageConfigPartial,
    UsageSummaryEntity,
    UsageTokensController,
} from '@domain';
import { PusherService } from 'src/app/shared/utils/pusher/pusher.service';

/** A partir desta fração do plano o saldo vira "atenção". */
export const USAGE_WARN_FRACTION = 0.75;
/** A partir desta fração do plano o saldo vira "crítico". */
export const USAGE_CRITICAL_FRACTION = 0.9;
/** Ação de consumo do modo "Editar com IA" (valor cru da API). */
export const USAGE_ACTION_AI_EDIT = 'ia_edit_document';
/** Canal e evento do backend a cada gasto de créditos. */
export const USAGE_TOKENS_CHANNEL_PREFIX = 'USAGE_TOKENS-';
export const USAGE_TOKENS_UPDATED_EVENT = 'usage-tokens-updated';
/** Espera para juntar vários gastos seguidos numa única releitura do resumo. */
export const USAGE_REFRESH_DEBOUNCE_MS = 800;

export type UsageLevel = 'ok' | 'warn' | 'critical' | 'exhausted';

/** Consumo do "Editar com IA" no período: pedidos e créditos. */
export interface UsageAiEditSummary {
    count: number;
    credits: number;
}

/**
 * Saldo de créditos da empresa no período corrente, para mostrar os limites de uso
 * dentro do editor (e em qualquer tela que precise do resumo sem recarregar).
 *
 * Carrega o resumo uma vez e depois acompanha ao vivo: o backend emite
 * `usage-tokens-updated` com o `usageConfig` novo a cada gasto; o saldo troca na
 * hora e a quebra por categoria é relida em seguida (com debounce).
 */
@Injectable({ providedIn: 'root' })
export class UsageLimitsStore {
    private readonly _controller = inject(UsageTokensController);
    private readonly _company = inject(CompanyController);
    private readonly _pusher = inject(PusherService);

    public readonly summary = signal<UsageSummaryEntity | null>(null);
    /** Carregando SEM dados na tela (a releitura silenciosa não liga isto). */
    public readonly loading = signal(false);
    /** Falhou e não há dados para mostrar. */
    public readonly error = signal(false);

    public readonly total = computed(() => nonNegative(this.summary()?.usageConfig?.totalTokens));
    public readonly used = computed(() => nonNegative(this.summary()?.usageConfig?.usedTokens));
    public readonly available = computed(() => Math.max(this.total() - this.used(), 0));

    /** Fração do plano já consumida (0..1). */
    public readonly usedFraction = computed(() => {
        const total = this.total();
        return total > 0 ? Math.min(this.used() / total, 1) : 0;
    });

    public readonly usedPercent = computed(() => Math.round(this.usedFraction() * 100));

    public readonly level = computed<UsageLevel>(() => {
        if (!this.summary()) return 'ok';
        if (this.available() <= 0) return 'exhausted';
        const fraction = this.usedFraction();
        if (fraction >= USAGE_CRITICAL_FRACTION) return 'critical';
        if (fraction >= USAGE_WARN_FRACTION) return 'warn';
        return 'ok';
    });

    /** Categorias que gastaram algo, maior consumo primeiro. */
    public readonly categories = computed<UsageCategorySummary[]>(() =>
        (this.summary()?.byCategory ?? [])
            .map(category => ({ ...category, sumTokens: nonNegative(category.sumTokens) }))
            .filter(category => category.sumTokens > 0)
            .sort((a, b) => b.sumTokens - a.sumTokens)
    );

    /** Quanto o "Editar com IA" consumiu no período, ou `null` se ainda não foi usado. */
    public readonly aiEdit = computed<UsageAiEditSummary | null>(() => {
        const action = (this.summary()?.byAction ?? []).find(a => a.action === USAGE_ACTION_AI_EDIT);
        if (!action || !(action.count > 0)) return null;
        return { count: action.count, credits: nonNegative(action.sumTokens) };
    });

    public readonly renewDate = computed<Date | null>(() => {
        const finish = this.summary()?.usageConfig?.finishPeriod;
        if (!finish) return null;
        const date = new Date(finish);
        return isNaN(date.getTime()) ? null : date;
    });

    /** Dias até a renovação (>= 0), contados a partir do momento da leitura. */
    public readonly daysToRenew = computed(() => {
        const date = this.renewDate();
        if (!date) return 0;
        const diff = Math.ceil((date.getTime() - this._now()) / (1000 * 60 * 60 * 24));
        return diff < 0 ? 0 : diff;
    });

    private _realtime?: Subscription;
    private _realtimeCompanyId?: string;
    private _refreshTimer?: ReturnType<typeof setTimeout>;
    private _request?: Subscription;

    /** Garante o resumo carregado e o acompanhamento ao vivo ligado. */
    public start(): void {
        this._subscribeRealtime();
        if (!this.summary() && !this.loading()) this.refresh(false);
    }

    /** Desliga o acompanhamento ao vivo (o resumo fica em cache). */
    public stop(): void {
        this._realtime?.unsubscribe();
        this._realtime = undefined;
        this._realtimeCompanyId = undefined;
        if (this._refreshTimer) clearTimeout(this._refreshTimer);
        this._refreshTimer = undefined;
    }

    /**
     * Relê o resumo. Silencioso (padrão) mantém o que está na tela enquanto busca e,
     * se falhar com dados, não troca nada; sem dados, qualquer leitura liga o loading.
     */
    public refresh(silent = true): void {
        this._request?.unsubscribe();
        const hasData = !!this.summary();
        if (!silent || !hasData) {
            this.loading.set(true);
            this.error.set(false);
        }

        this._request = this._controller.getSummary().subscribe({
            next: summary => {
                this.summary.set(summary);
                this.loading.set(false);
                this.error.set(false);
            },
            error: () => {
                this.loading.set(false);
                if (!this.summary()) this.error.set(true);
            },
        });
    }

    /** Limpa tudo (troca de empresa / logout). */
    public clear(): void {
        this.stop();
        this._request?.unsubscribe();
        this.summary.set(null);
        this.loading.set(false);
        this.error.set(false);
    }

    private _subscribeRealtime(): void {
        const companyId = this._company.getCurrentCompanyID();
        if (!companyId) return;
        if (this._realtime && this._realtimeCompanyId === companyId) return;

        this._realtime?.unsubscribe();
        this._realtimeCompanyId = companyId;
        this._realtime = this._pusher
            .subscribe(`${USAGE_TOKENS_CHANNEL_PREFIX}${companyId}`, USAGE_TOKENS_UPDATED_EVENT)
            .subscribe(payload => this._onRealtime(payload));
    }

    /** O saldo chega no evento e troca na hora; a quebra por categoria vem na releitura. */
    private _onRealtime(payload: { usageConfig?: Partial<UsageConfigPartial> } | null): void {
        const usageConfig = payload?.usageConfig;
        if (usageConfig) {
            this.summary.update(current =>
                current ? { ...current, usageConfig: { ...current.usageConfig, ...usageConfig } } : current
            );
        }

        if (this._refreshTimer) clearTimeout(this._refreshTimer);
        this._refreshTimer = setTimeout(() => {
            this._refreshTimer = undefined;
            this.refresh(true);
        }, USAGE_REFRESH_DEBOUNCE_MS);
    }

    /** Separado para os testes fixarem o relógio. */
    protected _now(): number {
        return Date.now();
    }
}

function nonNegative(value: unknown): number {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
}
